import { useCallback, useEffect, useState } from "react";
import { fetchAudienceInterests, isAbortError, type AudienceInterest } from "./api";

export interface InterestChoice {
  /** The value stored on the newsletter — a stable slug where there is one. */
  value: string;
  label: string;
  labelAr?: string;
  subscribers: number;
}

export type InterestsState =
  | { status: "loading"; choices: InterestChoice[]; retry: () => void }
  | { status: "ready"; choices: InterestChoice[]; approximate: boolean; retry: () => void }
  | { status: "error"; choices: InterestChoice[]; message: string; retry: () => void };

function toChoice(row: AudienceInterest): InterestChoice {
  const en = row.name_en?.trim();
  const ar = row.name_ar?.trim();
  return {
    // Prefer the slug: a topic renamed at the source should not orphan the
    // newsletters already targeting it.
    value: row.slug?.trim() || en || ar || String(row.id),
    label: en || ar || row.slug || String(row.id),
    labelAr: ar || undefined,
    subscribers: row.subscribers,
  };
}

/**
 * The topics a newsletter can be sent to.
 *
 * There is deliberately no built-in fallback list. An invented topic is one
 * no subscriber chose, so a newsletter targeting it would reach nobody — and
 * an empty picker that says why is more use than a full one that lies.
 */
export function useAudienceInterests(): InterestsState {
  // A failed load left the writer on the one required step with no way
  // forward and nothing to press. Asking again is the obvious move and it
  // was not offered.
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const [state, setState] = useState<InterestsState>({ status: "loading", choices: [], retry });

  useEffect(() => {
    const ac = new AbortController();
    setState({ status: "loading", choices: [], retry });
    (async () => {
      try {
        const { interests, approximate } = await fetchAudienceInterests(ac.signal);
        if (ac.signal.aborted) return;
        setState({ status: "ready", choices: interests.map(toChoice), approximate, retry });
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        setState({
          status: "error",
          choices: [],
          message: err instanceof Error ? err.message : "Subscriber interests are not reachable.",
          retry,
        });
      }
    })();
    return () => ac.abort();
  }, [attempt, retry]);

  return state;
}
